import React from 'react'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import moment from 'moment'
import _ from 'lodash'

import formatNumber from './formatNumber'
import momenttz from './momenttz'

export const formatVehicleName = (cell, row) => {
	return row.vehicle_name || row.device_name || row.device_id.toString()
}

export const formatDataUsage = (cell, row, companyData) => {
	const dataLimit = 1

	return <span className={`${((row.data_usage / 1024 / 1024 / 1024) > dataLimit) ? 'error-label' : ''}`}>{(row.data_usage ? (row.data_usage / 1024 / 1024 / 1024).toFixed(2) : '0.00') + ' / ' + dataLimit + ' GB'}</span>
}

export const formatDataUsageCSV = (cell, row, companyData) => (row.data_usage / 1024 / 1024 / 1024).toFixed(2) + ' / ' + (row.data_limit ? row.data_limit : companyData.data_limit) + ' (GB)'

export const formatChannelStatus = (cell, row, onClick = null) => {
	let failureOverlays = [];
	if (cell && cell.length) {
		failureOverlays = cell.reduce((totalFailures, channel) => {
			if (channel.fail_ts > (channel.success_ts || '1970-01-01 00:00:00') && row.channel_available.includes(channel.channel)) {
				totalFailures.length && totalFailures.push(', ');
				totalFailures = [
					...totalFailures,
					<OverlayTrigger
						key={channel.channel}
						placement="bottom"
						delayHide={100}
						overlay={<Tooltip id="vehicle-name-tooltip" className="tooltip custom-tooltips">
							{channel.fail_status
								? <span>Status: {formatFailStatus(channel.fail_status)}</span>
								: <span>Possible Failure Detected</span>
							}
						</Tooltip>}
					>
						{onClick
							? <span role="button" tabIndex="0" onClick={() => onClick(row, channel)}>{`ch${channel.channel}`}</span>
							: <span>{`ch${channel.channel}`}</span>
						}
					</OverlayTrigger>,
				]
			}
			return totalFailures
		}, [])
	}
	return failureOverlays.length ? <div className="error-label">{failureOverlays}</div> : <span>OK</span>
}

export const formatChannelStatusCSV = (cell, row) => {
	let failures = '';
	if (row.channel_status && row.channel_status.length) {
		failures = row.channel_status.reduce((channelFailureStr, channel) => {
			if (channel.fail_ts > (channel.success_ts || '1970-01-01 00:00:00') && row.channel_available.includes(channel.channel)) {
				channelFailureStr += `${channelFailureStr.length ? ', ' : ''}ch${channel.channel}`
			}
			return channelFailureStr
		}, '')
	}
	return failures || 'OK'
}

export const formatFailStatus = (status) => {
	if (status === 'solid') {
		return 'Possible failure detected'
	}
	return status.charAt(0).toUpperCase() + status.slice(1) + ' screen detected'
}

export const formatRecordingStatus = (cell) => {
	if (_.isEmpty(cell) || cell.count === 0) {
		return <span>OK</span>
	}
	return <span className="error-label">Needs Reformat</span>
}

export const formatRecordingStatusCSV = (cell) => ((_.isEmpty(cell) || cell.count === 0) ? 'OK' : 'Needs Reformat')

export const formatOnlineStatus = (cell, row) => {
	if (!row.online) {
		return (
			<div>
				<span>Offline </span>
				{row.last_disconnected_ts && <span><span>since</span><div>{momenttz(`${row.last_disconnected_ts}+00`).format('M/D/YY h:mm:ss A')}</div></span>}
			</div>
		)
	}

	const ignitionOffTS = row.ignition && row.ignition.ts ? row.ignition.ts : ''; // 2023-01-04 18:50:37
	const duration = moment.duration(moment.utc().diff(moment.utc(ignitionOffTS), 'seconds'), 'seconds')

	// FWSD-4314 - If device does not receive GPS from device and ignition is Off more 24 hours (1440 mins) then display Online status.
	// Device stopped receive GPS, broken antena or something, and FileServer does not know when ignition is On.
	if ((row.ignition && row.ignition.state) /*|| (!row.gps_online && durationInMins > 1440)*/) {
		return <span className="online-label">Online</span>
	}
	return (
		<div className="parked-label">
			<span>Parked for</span>
			<div>{moment(duration).format('hh:mm:ss', { trim: false })}</div>
		</div>
	)
}

export const formatOnlineStatusCSV = (cell, row) => {
	const getTime = () => {
		if (row.status === 'parked' && row.ignition && row.ignition.ts) {
			 return ' '+moment.duration(moment.utc().diff(moment.utc(row.ignition.ts || ''), 'seconds'), 'seconds').format('hh:mm:ss', { trim: false })
		}
		if (row.status === 'offline' && row.last_disconnected_ts) {
			 return ` Since ${formatTimestamp(row.last_disconnected_ts, true)}`
		}

		return ''
	}

	return row.status.charAt(0).toUpperCase() + row.status.slice(1) + getTime()
}

export const formatGroup = (cell, row) => {
	if (row.group_name) {
		return row.group_name
	}
	return (row.vehicle_group && row.vehicle_group.length) ? row.vehicle_group.join(', ') : '';
}

export const formatTimestamp = (cell, isGMT = false) => {
	// Cell can be as "Fri, 13 Aug 2021 14:21:37 GMT" or "2021-11-29 16:00:00"
	let timestamp = cell
	if (cell && cell.timestamp) {
		timestamp = cell.timestamp
	}
	if (cell && cell.uploaded_timestamp) {
		timestamp = cell.uploaded_timestamp
	}

	if (isGMT) {
		return timestamp ? momenttz(`${timestamp}`).format('M/D/YY h:mm:ss A') : '-'
	}

	return timestamp ? momenttz(`${timestamp}+00`).format('M/D/YY h:mm:ss A') : '-'
}

export const formatFirmwareStatus = (cell) => {
	if (cell === 'latest firmware') {
		return <span className="online-label">Latest</span>
	}
	if (cell === 'test firmware') {
		return <span>Test Firmware</span>
	}
	if (cell === 'firmware out of date') {
		return <span className="error-label">Out Of Date</span>
	}
	return <span className="warning-label">Upgrade Sent</span>
}

export const formatFirmwareCSV = (cell) => {
	if (cell === 'latest firmware') {
		return 'Latest'
	}
	if (cell === 'test firmware') {
		return 'Test Firmware'
	}
	if (cell === 'firmware out of date') {
		return 'Out Of Date'
	}

	return 'Upgrade Sent'
}

export const dataFormat = name => (cell, row) => (
	zeroAndRounding(totalValue(row[`bucket_usage_${name}`], row[`live_usage_${name}`]))
);

export const floatFormat = name => (cell, row) => (
	formatNumber(zeroAndRounding(row[name]))
);

export const totalValue = (a, b) => a + b;

export const zeroAndRounding = val => (
	(val && Number(val) !== 0) ? parseFloat(val.toFixed(2)) : '-'
);

export const customSortFunc = name => (a, b, order) => {
	const first = totalValue(a[`bucket_usage_${name}`], a[`live_usage_${name}`]);
	const second = totalValue(b[`bucket_usage_${name}`], b[`live_usage_${name}`]);
	return order === 'asc' ? second - first : first - second
};

export const vehicleNameSortFunc = (a, b, order) => {
	const first = (a.vehicle_name || a.device_id.toString())
	const second = (b.vehicle_name || b.device_id.toString())
	return order === 'asc' ? second.localeCompare(first) : first.localeCompare(second)
};
